<template>
  <RevLoadingScreen
    v-if="isLoadingCategories"
    class="mt-48"
    :text="i18n(translations.loadingText)"
  />

  <div v-else class="grid gap-y-24">
    <p class="body-1-bold mt-32 text-center">
      {{ i18n(translations.mainCategoriesText) }}
    </p>
    <div class="mt-12 grid grid-cols-5 gap-20">
      <RevPicker
        v-for="category in mainCategories"
        :key="category.value"
        :label="category.label"
        :model-value="selectedCategory?.value === category.value"
        @update:model-value="handleSelectCategory(category)"
      />
    </div>

    <p>
      {{ i18n(translations.otherCategoriesText) }}
    </p>

    <RevInputSelectSearchable
      v-if="!isEmpty(otherCategories)"
      id="category-select"
      v-model="selectedCategory"
      data-qa="category-select"
      :hide-checkboxes="true"
      :label="i18n(translations.categorySelectLabel)"
      :no-options-message="i18n(translations.noOptionsMessage)"
      :options="filteredOtherCategories"
      @text-change="handleSearchTextChange"
    />

    <div class="mx-auto mt-12 grid w-[328px] grid-cols-1 gap-24">
      <RevButton
        class=""
        data-qa="submit-category-button"
        :disabled="!selectedCategory"
        variant="primary"
        @click="emit('submit', selectedCategory)"
      >
        {{ i18n(translations.submitButton) }}
      </RevButton>
      <RevButton
        data-qa="reset-category-button"
        :disabled="!selectedCategory"
        variant="secondary"
        @click="handleResetSelectedCategory"
      >
        {{ i18n(translations.resetButton) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevInputSelectSearchable } from '@ds/components/InputSelectSearchable'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevPicker } from '@ds/components/Picker'

import type { ProductCategory } from '~/api/products/types'

import translations from './CategoryChoiceStep.translations'

const i18n = useI18n()

const emit = defineEmits(['submit'])
const props = defineProps<{
  productCategories: ProductCategory[]
  isLoadingCategories: boolean
  activeCategory: ProductCategory | null
}>()

type ProductCategoryOption = {
  id: string
  label: string
  value: string
}

const selectedCategory = ref<ProductCategoryOption | null>(
  props.activeCategory
    ? {
        id: props.activeCategory.value,
        label: props.activeCategory.label,
        value: props.activeCategory.value,
      }
    : null,
)

const searchText = ref('')

const mainCategories = computed(() => {
  return props.productCategories ? props.productCategories.slice(0, 10) : []
})

const otherCategories = computed(() => {
  if (!props.productCategories) {
    return []
  }

  return props.productCategories
    .slice(10)
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((category) => ({
      id: category.value,
      label: category.label,
      value: category.value,
    })) as ProductCategoryOption[]
})

const filteredOtherCategories = computed(() => {
  if (!searchText.value) {
    return otherCategories.value
  }

  return otherCategories.value.filter((category) =>
    category.label.toLowerCase().includes(searchText.value.toLowerCase()),
  )
})

function handleSelectCategory(category: ProductCategory) {
  selectedCategory.value = category
    ? {
        id: category.value,
        label: category.label,
        value: category.value,
      }
    : category
}

function handleResetSelectedCategory() {
  selectedCategory.value = null
}

const handleSearchTextChange = useDebounceFn(
  function debouncedHandleSearchTextChange(value: string) {
    searchText.value = value
  },
  300,
)
</script>
